import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { CheckCircle, X, Target, UserCheck, TrendingUp, Award } from 'lucide-react';

const ForWhoSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const yesPoints = [
    'Eres nutricionista independiente o tienes una clínica pequeña',
    'Quieres automatizar el seguimiento sin perder tu toque personal',
    'Buscas mejorar la retención y resultados de tus pacientes',
    'Estás listo para diferenciarte con tecnología de vanguardia',
    'Valoras tu tiempo y quieres enfocarte en consultas de alto valor',
  ];

  const noPoints = [
    'Buscas reemplazar completamente la atención personal',
    'No tienes pacientes activos actualmente',
    'Prefieres el seguimiento 100% manual tradicional',
    'No usas WhatsApp en tu práctica',
  ];

  return (
    <section ref={ref} className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-[#FAFAF9]" data-testid="for-who-section">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto mb-12 md:mb-16"
        >
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#849B89] mb-4">
            ¿Es para ti?
          </span>
          <h2 className="font-cabinet text-3xl md:text-4xl font-bold text-[#1A2E24] tracking-tight mb-4">
            HabitIA es perfecto para ti si...
          </h2>
        </motion.div>

        {/* Cards Grid */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
          {/* YES Card */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white rounded-3xl border border-[#2E5C41]/20 p-8 md:p-10 hover-lift"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#2E5C41] flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-white" strokeWidth={1.5} />
              </div>
              <h3 className="font-cabinet text-xl font-bold text-[#2E5C41]">Sí, es para ti</h3>
            </div>
            <ul className="space-y-4">
              {yesPoints.map((point, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <CheckCircle className="w-5 h-5 text-[#2E5C41] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <span className="text-sm text-[#4B5E53]">{point}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* NO Card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-[#F2F4F0] rounded-3xl border border-[#2E5C41]/10 p-8 md:p-10"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#4B5E53]/20 flex items-center justify-center">
                <X className="w-6 h-6 text-[#4B5E53]" strokeWidth={1.5} />
              </div>
              <h3 className="font-cabinet text-xl font-bold text-[#4B5E53]">No es para ti si...</h3>
            </div>
            <ul className="space-y-4">
              {noPoints.map((point, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: 10 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.3, delay: 0.4 + index * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <X className="w-5 h-5 text-[#4B5E53]/50 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <span className="text-sm text-[#4B5E53]/70">{point}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-12 md:mt-16"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-4xl mx-auto">
            {[
              { icon: Target, value: '10-50', label: 'pacientes ideal' },
              { icon: UserCheck, value: 'Independientes', label: 'o clínicas pequeñas' },
              { icon: TrendingUp, value: 'LATAM', label: 'mercado inicial' },
              { icon: Award, value: 'WhatsApp', label: 'como canal principal' },
            ].map((stat, index) => (
              <div
                key={index}
                className="text-center p-4 md:p-6 bg-white rounded-2xl border border-[#2E5C41]/10"
              >
                <stat.icon className="w-6 h-6 text-[#2E5C41] mx-auto mb-2" strokeWidth={1.5} />
                <p className="font-cabinet font-bold text-lg text-[#1A2E24]">{stat.value}</p>
                <p className="text-xs text-[#4B5E53]">{stat.label}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ForWhoSection;
